import { DOCUMENT } from '@angular/common';
import {
    afterNextRender,
    ChangeDetectionStrategy,
    Component,
    computed,
    inject,
    input,
    type OnChanges,
    type OnDestroy,
    type OnInit,
    signal,
    type SimpleChanges,
    type TemplateRef,
    viewChild
} from '@angular/core';
import { LocalStorageService } from '@df/browser/storage/local-storage.service';
import type { IProductDataV3 } from '@df/catalog/product/product';
import { App } from '@df/core/app';
import { ElementHeightDirective } from '@df/dom/element-height/element-height.directive';
import { AbService } from '@df/module-ab/ab.service';
import { GtmService } from '@df/module-gtm/gtm.service';
import { OffClickDirective } from '@df/module-off-click/off-click.directive';
import { ProductConfigurationConfigurable } from '@df/module-product-configuration/model/configurable/product-configuration-configurable';
import { ProductConfiguration } from '@df/module-product-configuration/model/product-configuration';
import { AfterContentInitDirectiveService } from '@df/ui/common/after-content-init.directive';
import { filterMediaByLabel } from '@df/ui/common/filter-media-by-label-pipe';
import { DfModalService } from '@df/ui/modal/df-modal.service';
import type { ModalRef } from '@df/ui/modal/modal-ref';
import { ModalService } from '@df/ui/modal/modal.service';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { ScrollService } from '@df/util/service/scroll.service';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent } from '@icms/core/content';
import { Nexus } from '@tomandco/nexus-api-types';
import { Subject } from 'rxjs/internal/Subject';
import { Subscription } from 'rxjs/internal/Subscription';
import { AbstractCmsBlockComponent } from '../../../../cms/block/abstract-cms-block.component';
import { CmsOutletBlockComponent } from '../../../../cms/outlet/block/cms-outlet-block.component';
import { StickyFreeComponent } from '../../../../common/sticky-free/sticky-free.component';
import { PageHeaderService } from '../../../../layout/page/header/page-header.service';
import { ProductDecorator } from '../../../model/product/product.decorator';
import { RelatedProductsComponent } from '../../related-products/related-products.component';
import { NBackInStockModal } from '../back-in-stock/back-in-stock-modal.interface';
import { ProductLayoutAddToBasketPanelComponent } from './add-to-basket/panel/product-layout-add-to-basket-panel.component';
import { ProductLayoutAddToBasketComponent } from './add-to-basket/product-layout-add-to-basket.component';
import { ProductLayoutConfigurableOptionsComponent } from './configurable-options/product-layout-configurable-options.component';
import { ProductLayoutDetailsComponent } from './details/product-layout-details.component';
import { ProductLayoutMediaComponent } from './media/product-layout-media.component';
import { PRODUCT_LAYOUT_CONFIG } from './product-layout.config';
import { NProductLayout } from './product-layout.interface';
import { ProductLayoutSidebarComponent, TSidebar } from './sidebar/product-layout-sidebar.component';
import { ProductLayoutViewComponent } from './view/product-layout-view.component';
import { ProductLayoutWishlistComponent } from './wishlist/product-layout-wishlist.component';
import { ProductLayoutWishlistService, WishlistState } from './wishlist/product-layout-wishlist.service';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            giftboxesEnabled: boolean;
        }
    }
}

@Component({
    selector: 'product-layout',
    templateUrl: './product-layout.component.html',
    styleUrl: './product-layout.component.scss',
    imports: [
        CmsOutletBlockComponent,
        ElementHeightDirective,
        ICmsClientModule,
        OffClickDirective,
        ProductLayoutAddToBasketComponent,
        ProductLayoutAddToBasketPanelComponent,
        ProductLayoutConfigurableOptionsComponent,
        ProductLayoutDetailsComponent,
        ProductLayoutMediaComponent,
        ProductLayoutSidebarComponent,
        ProductLayoutWishlistComponent,
        RelatedProductsComponent,
        StickyFreeComponent,
        TranslatePipe
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
@ICmsComponent<ProductLayoutComponent>(PRODUCT_LAYOUT_CONFIG)
export class ProductLayoutComponent
    extends AbstractCmsBlockComponent
    implements NICmsContentComponent.IComponent, OnInit, OnChanges, OnDestroy
{
    @ICmsContent({
        type: 'blockContent'
    })
    override content!: NProductLayout.IContent;

    currentMediaLayout = signal<NProductLayout.EMediaLayout>(NProductLayout.EMediaLayout.scroll);

    readonly product = input.required<ProductDecorator<IProductDataV3>>();
    readonly parent = input.required<ProductDecorator<IProductDataV3>>();

    readonly productLayoutConfigurableOptionsModalTpl = viewChild<TemplateRef<unknown>>('productLayoutConfigurableOptionsModalTpl');

    /**
     * Parent is already detached
     */
    override canDetach = false;

    readonly media = signal<Nexus.Lib.Catalog.Type.IProductMediaItem[]>([]);
    readonly mediaLayout = NProductLayout.EMediaLayout;

    readonly addToBasketPanelVisible = signal(false);

    sidebar?: TSidebar;

    productChange = new Subject<ProductDecorator<IProductDataV3>>();
    detectChanges$ = new Subject<void>();

    labelDiscount = false;
    productConfigurableOptionsModalIsPalette = false;

    readonly mediaLayoutGrid = computed(() => {
        return (
            this.currentMediaLayout() === NProductLayout.EMediaLayout.grid &&
            this.media().length > 1 &&
            (this.client.isL || this.client.isX)
        );
    });

    readonly productView = inject(ProductLayoutViewComponent);
    readonly productConfiguration = inject(ProductConfiguration) as ProductConfigurationConfigurable;
    private readonly app = inject(App);
    private readonly abService = inject(AbService);
    private readonly gtmService = inject(GtmService);
    private readonly afterContentInit = inject(AfterContentInitDirectiveService);
    private readonly pageHeaderService = inject(PageHeaderService);
    private readonly dfModalService = inject(DfModalService);
    private readonly modalService = inject(ModalService);
    private readonly productLayoutWishlistService = inject(ProductLayoutWishlistService);
    private readonly localStorageService = inject(LocalStorageService);
    private readonly scrollService = inject(ScrollService);
    private document = inject(DOCUMENT);

    /**
     * If init unlock is required
     */
    private unlockRequired = true;

    private _isSidebarOpen = false;
    private sidebarDetectChangesRafId?: number;
    private abServiceSubscription?: Subscription;
    private productConfigurableOptionsModalRef?: ModalRef<unknown, { isPalette: boolean }>;
    private trayScrollSubscription?: Subscription;

    constructor() {
        super();

        afterNextRender(() => {
            const hasLayoutGrid = this.document.querySelector('._media._layout-grid');

            if (hasLayoutGrid) {
                this.currentMediaLayout.set(NProductLayout.EMediaLayout.grid);
                this.detectChanges();
            }
        });
    }

    get cmsTemplate(): string | undefined {
        const cmsTemplate = this.product()?.data?.pdp_cms_template;

        if (cmsTemplate) {
            return cmsTemplate.toString();
        }

        return undefined;
    }

    get showKlarna(): boolean {
        return this.product()?.isKlarnaAllowed && this.content.allowFinancingTray;
    }

    get sizeGuideVariant(): string | undefined {
        const sizeGuideId = this.productView?.model?.sizeGuideId;

        if (sizeGuideId) {
            return sizeGuideId.toString();
        }

        return undefined;
    }

    get isConfigurableOptionsModalOpen(): boolean {
        return !!this.productConfigurableOptionsModalRef;
    }

    /*
     * Checks if any sidebar is open
     */
    get isSidebarOpen() {
        return this._isSidebarOpen && !!this.sidebar;
    }

    set isSidebarOpen(val: boolean) {
        this._isSidebarOpen = val;

        this.scheduleSidebarDetectChanges();
    }

    private scheduleSidebarDetectChanges(): void {
        if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function' || this.client.mode !== 'browser') {
            this.detectChanges();

            return;
        }

        if (this.sidebarDetectChangesRafId) {
            cancelAnimationFrame(this.sidebarDetectChangesRafId);
        }

        this.sidebarDetectChangesRafId = window.requestAnimationFrame(() => {
            this.sidebarDetectChangesRafId = undefined;
            this.detectChanges();
        });
    }

    override ngOnInit() {
        super.ngOnInit();

        const mediaLayout = this.localStorageService.get('mediaLayout');

        if (mediaLayout && (this.client.isL || this.client.isX)) {
            this.handleMediaLayoutChange(mediaLayout as NProductLayout.EMediaLayout);
        } else {
            this.handleMediaLayoutChange(NProductLayout.EMediaLayout.scroll);
        }

        this.onProductChange();

        this.subscriptions.add(this.app.customer.model.obs.status.subscribe(() => this.detectChanges()));
        this.subscriptions.add(
            this.app.client.obs.breakpoint.subscribe((sizeName: string) => {
                if (sizeName === 'S' || sizeName === 'M') {
                    this.handleMediaLayoutChange(NProductLayout.EMediaLayout.scroll);
                }

                this.closeProductLayoutConfigurableOptionsModal();
            })
        );

        this.abServiceSubscription = this.abService.getSubject('labelDiscount').subscribe(value => {
            if (value.variant === 'B') {
                this.labelDiscount = true;
            } else {
                this.labelDiscount = false;
            }
            this.detectChanges();
        });

        this.unlock();
    }

    override ngOnChanges(changes: SimpleChanges): void {
        if (changes['product']) {
            this.onProductChange();
        }

        super.ngOnChanges(changes);
    }

    override ngOnDestroy() {
        this.abServiceSubscription?.unsubscribe();
        this.closeProductLayoutConfigurableOptionsModal();
        this.trayScrollSubscription?.unsubscribe();
        if (this.sidebarDetectChangesRafId) {
            cancelAnimationFrame(this.sidebarDetectChangesRafId);
            this.sidebarDetectChangesRafId = undefined;
        }

        super.ngOnDestroy();

        // reset page header settings
        this.timeoutService.platformSafe(() => this.pageHeaderService.resetHighPriorityState()); // injector may be destroyed
    }

    handleMediaLayoutChange(event: NProductLayout.EMediaLayout): void {
        this.currentMediaLayout.set(event);

        this.localStorageService.set('mediaLayout', event);

        this.detectChanges();
    }

    handleProductNameVisible(event: boolean) {
        this.addToBasketPanelVisible.set(!event);

        this.detectChanges();
    }

    openStockNotification() {
        if (this.client.isL || this.client.isX) {
            this.openSidebar('stock-notification');
        } else {
            this.openStockNotificationModal();
        }
    }

    openStockNotificationModal() {
        this.dfModalService.open(undefined, NBackInStockModal.ID, undefined, this.product());
    }

    openProductLayoutConfigurableOptionsModal(isPalette = false) {
        const modalTpl = this.productLayoutConfigurableOptionsModalTpl();

        if (modalTpl && !this.productConfigurableOptionsModalRef) {
            this.productConfigurableOptionsModalIsPalette = isPalette;

            this.productConfigurableOptionsModalRef = this.modalService.open<unknown, { isPalette: boolean }>(
                modalTpl,
                {
                    minWidth: undefined,
                    maxWidth: undefined,
                    height: '100%',
                    width: '100%',
                    position: {
                        right: '0',
                        bottom: '0',
                        left: '0'
                    },
                    hasBackdrop: true,
                    scrollStrategy: 'noop',
                    modalClass: 'p-l-0 p-r-0 p-b-0 flex-column flex-justify-end',
                    data: {
                        isPalette
                    },
                    onOpen: () => {
                        this.trayScrollSubscription = this.scrollService.scroll$.subscribe(() => {
                            this.closeProductLayoutConfigurableOptionsModal();
                        });
                    },
                    onClose: () => {
                        this.trayScrollSubscription?.unsubscribe();
                    }
                },
                'bottom'
            );
        }
    }

    closeProductLayoutConfigurableOptionsModal() {
        this.productConfigurableOptionsModalRef?.close();
        this.productConfigurableOptionsModalRef = undefined;
        this.productConfigurableOptionsModalIsPalette = false;

        if (this.productLayoutWishlistService.state() === WishlistState.BeforeAdd) {
            this.productLayoutWishlistService.reset();
        }
    }

    /////////////////// SIDEBAR ////////////////////

    /**
     * Open details sidebar
     */
    openSidebar(sidebar: TSidebar) {
        this.sidebar = sidebar;
        this._isSidebarOpen = true;

        this.sendGtmEvent('open' + sidebar.charAt(0).toUpperCase() + sidebar.slice(1));

        this.detectChanges();
    }

    /////////////////// HELPERS ////////////////////

    override detectChanges(changes?: NICmsContentComponent.IDetectionChanges) {
        const watchPath = ['content', 'variant', 'image', 'video_id'];

        if (
            watchPath.some(path => (changes?.['content'] as NICmsContentComponent.Mutation)?.changeEvent?.valuePath?.includes(path)) ||
            watchPath.some(path => !!(changes?.['content'] as NICmsContentComponent.Change)?.currentValue?.[path])
        ) {
            this.updateClassNames();
            this.updateMedia();
        }

        if (this.client.isCms) {
            this.productView.detectChanges();
        }

        if (this.content?.mediaLayout && !this.currentMediaLayout()) {
            this.currentMediaLayout.set(this.content.mediaLayout);
        }

        super.detectChanges();

        this.detectChanges$.next();

        this.unlock();
    }

    ///////////////// MEDIA //////////////

    private updateMedia() {
        this.media.set(this.getMedia());
    }

    /**
     * Retrieves allowed media for gallery
     */
    private getMedia(): Nexus.Lib.Catalog.Type.IProductMediaItem[] {
        let media = this.product()?.data?.media || [];

        // filter media if needed
        const [bannedMediaLabels, requiredMediaLabels] = [this.content?.bannedMediaLabels, this.content?.requiredMediaLabels].map(list =>
            list
                ?.split(',')
                .map((i: string) => i.trim().toLowerCase())
                .filter(i => !!i)
        );

        if (requiredMediaLabels?.length) {
            media = filterMediaByLabel(media, requiredMediaLabels, false) || [];
        } else if (bannedMediaLabels?.length) {
            media = filterMediaByLabel(media, bannedMediaLabels, true) || [];
        }

        return media.map(item => ({ ...item, landscape: Array.isArray(item.labels) && item.labels.includes('landscape') }));
    }

    private onProductChange() {
        this.updateMedia();

        this.productChange.next(this.product());

        this.detectChanges();
    }

    private unlock() {
        if (this.unlockRequired && this.content) {
            this.unlockRequired = false;
            this.timeoutService.setTimeout(() => {
                this.afterContentInit.unlock(this.productView);
            }, 500);
        }
    }

    private updateClassNames(): void {
        // fallback to old saved data
        const _transparent =
            this.content?.headerTransparent === undefined
                ? (<any>this.content)?.header_transparent
                : this.content?.headerTransparent || false;
        const _lightMode =
            this.content?.headerLightMode === undefined ? (<any>this.content)?.header_light_mode : this.content?.headerLightMode || false;

        const transparent = _transparent === undefined ? true : !!_transparent;
        const lightMode = transparent && !!_lightMode;

        this.pageHeaderService.setHighPriorityState({
            transparent,
            lightMode,
            hideUp: false
        });
    }

    private sendGtmEvent(event: string): void {
        this.gtmService.push({ event });
    }
}
